import { Controller, useFormContext } from "react-hook-form";

import type { DropdownIndicatorProps, GroupBase, InputProps, OptionProps, SingleValueProps } from "@des/react-select";
import ReactSelect, { components } from "@des/react-select";

import { ChevronDownIcon } from "@jti/brand-icons/chevron-down";
import { UiCheckIcon } from "@jti/brand-icons/ui-check";
import { Badge } from "@jti/core-components/components/Badge";
import { type ProductDetailsProductData } from "@jti/fe-common";
import { formatPrice } from "@jti/format-price";
import { ConsumablesFormStepSection } from "@jti/ui/components/ConsumablesFormStepSection";
import { Tooltip } from "@jti/ui/components/Tooltip";
import { useTranslations } from "@jti/ui/providers/useTranslations";

import type { ConsumablesFormValues } from "../../ConsumablesForm.types";
import type { ValidTierPrice } from "../../utils/getValidTierPrices";
import { ConsumablesSectionTitle } from "../ConsumablesFormSectionTitle";

import styles from "./ConsumablesPriceDropdown.module.scss";

type TierPriceOption = {
  value: string;
  label: string;
  tierPrice: ValidTierPrice;
  regularPrice?: ProductDetailsProductData["regularPrice"];
};

type TierPriceGroup = GroupBase<TierPriceOption>;

type ConsumablesPriceDropdownProps = {
  step: number;
  tierPrices: ValidTierPrice[];
  regularPrice?: ProductDetailsProductData["regularPrice"];
  message?: string;
  tooltip?: string;
};

const getRegularTotalPrice = (tierPrice: ValidTierPrice, regularPrice: ProductDetailsProductData["regularPrice"]) => {
  if (regularPrice?.currency !== tierPrice.totalPrice.currency) {
    return undefined;
  }

  return {
    value: regularPrice.value * tierPrice.quantity,
    currency: regularPrice.currency,
  };
};

const TierPriceContent = ({ option, showCheckmark }: { option: TierPriceOption; showCheckmark?: boolean }) => {
  const regularTotalPrice = getRegularTotalPrice(option.tierPrice, option.regularPrice);

  return (
    <div className={styles.tierPriceContent}>
      <div className={styles.tierDescription}>
        <span className={styles.tierLabel} title={`${option.tierPrice.quantity} ${option.tierPrice.label}`}>
          {option.tierPrice.quantity} {option.tierPrice.label}
        </span>
        {option.tierPrice.discount.percentOff > 0 && (
          <Badge variant="sale" label={`-${option.tierPrice.discount.percentOff}%`} />
        )}
      </div>
      <span className={styles.priceContent}>
        <strong className={styles.finalPrice}>{formatPrice(option.tierPrice.totalPrice)}</strong>
        {regularTotalPrice && regularTotalPrice.value > option.tierPrice.totalPrice.value && (
          <del className={styles.regularPrice}>{formatPrice(regularTotalPrice)}</del>
        )}
      </span>
      {showCheckmark && <UiCheckIcon className={styles.checkmark} aria-hidden="true" />}
    </div>
  );
};

const TierPriceOptionComponent = (props: OptionProps<TierPriceOption, false, TierPriceGroup>) => {
  return (
    <components.Option {...props} innerProps={{ ...props.innerProps, "aria-label": props.data.label }}>
      <TierPriceContent option={props.data as TierPriceOption} showCheckmark={props.isSelected} />
    </components.Option>
  );
};

const TierPriceSingleValue = (props: SingleValueProps<TierPriceOption, false, TierPriceGroup>) => (
  <components.SingleValue {...props}>
    <TierPriceContent option={props.data} />
  </components.SingleValue>
);

const TierPriceDropdownIndicator = (props: DropdownIndicatorProps<TierPriceOption, false, TierPriceGroup>) => (
  <components.DropdownIndicator {...props}>
    <ChevronDownIcon aria-hidden="true" />
  </components.DropdownIndicator>
);

const TierPriceInput = (props: InputProps<TierPriceOption, false, TierPriceGroup>) => {
  const selectedOption = props.selectProps.value as TierPriceOption | null;
  const value = props.isHidden && selectedOption ? selectedOption.label : props.value;

  return <components.Input {...props} value={value} />;
};

export const ConsumablesPriceDropdown = ({
  step,
  tierPrices,
  regularPrice,
  message,
  tooltip,
}: ConsumablesPriceDropdownProps) => {
  const { __ } = useTranslations();
  const { control } = useFormContext<ConsumablesFormValues>();
  const options: TierPriceOption[] = tierPrices.map((tierPrice) => ({
    value: String(tierPrice.quantity),
    label: __("consumablesForm.step.price_dropdown.option", [
      tierPrice.quantity,
      tierPrice.label,
      formatPrice(tierPrice.finalPrice),
      formatPrice(tierPrice.totalPrice),
      tierPrice.discount.percentOff,
    ]),
    tierPrice,
    regularPrice,
  }));

  return (
    <ConsumablesFormStepSection
      title={
        <ConsumablesSectionTitle translationKey="consumablesForm.step.quantity_selection.title" stepCount={step} />
      }
      data-testid="price-dropdown-selection-step"
    >
      {message && (
        <div className={styles.message} data-testid="consumables-price-dropdown-message">
          <span className={styles.messageText}>{message}</span>
          {tooltip && <Tooltip content={<span>{tooltip}</span>} size="small" />}
        </div>
      )}
      <Controller
        control={control}
        name="quantity"
        render={({ field }) => (
          <ReactSelect<TierPriceOption, false, TierPriceGroup>
            inputId="consumables-price-dropdown"
            aria-label={__("consumablesForm.step.quantity_selection.title", [step])}
            data-testid="consumables-price-dropdown"
            options={options}
            isSearchable={false}
            value={options.find((option) => option.value === field.value) ?? null}
            onChange={(option) => option && field.onChange(option.value)}
            className={styles.dropdown}
            classNamePrefix="react-select"
            unstyled={true}
            components={{
              Option: TierPriceOptionComponent,
              SingleValue: TierPriceSingleValue,
              DropdownIndicator: TierPriceDropdownIndicator,
              IndicatorSeparator: null,
              Input: TierPriceInput,
            }}
          />
        )}
      />
    </ConsumablesFormStepSection>
  );
};
